import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CandlestickChart, ShieldCheck, LineChart, Lock } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function Login() {
  const { user, loading, googleLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  const onGoogleSuccess = async (resp) => {
    try {
      await googleLogin(resp.credential);
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign-in failed. Please try again.");
    }
  };

  const legacySignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--bg)", color: "var(--text)" }} data-testid="login-page">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden"
        style={{ background: "linear-gradient(155deg,#0c0c12 0%,#12101f 55%,#0a0a0c 100%)", borderRight: "1px solid var(--border)" }}>
        <div className="flex items-center gap-3">
          <div className="grid place-items-center rounded-xl" style={{ width: 42, height: 42, background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", boxShadow: "0 8px 26px -8px rgba(139,92,246,.7)" }}>
            <CandlestickChart size={22} color="#fff" strokeWidth={2.4} />
          </div>
          <div className="leading-tight">
            <div className="text-lg font-extrabold tracking-wide">YUMAERA</div>
            <div className="text-[10px] font-semibold tracking-[0.28em]" style={{ color: "var(--accent-soft)" }}>TRADEPLAN</div>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="text-4xl font-extrabold leading-tight">Institutional-grade<br />crypto trade planning.</h1>
          <p className="mt-4 text-sm" style={{ color: "var(--text-muted)" }}>
            Persistent 4H swing setups, live KuCoin / OKX data, and disciplined risk—now behind a secure, invite-based workspace.
          </p>
          <div className="mt-8 space-y-3">
            {[["Locked swing setups & target tracking", LineChart],
              ["Encrypted, role-based access control", ShieldCheck],
              ["Private workspace, owner-approved API access", Lock]].map(([t, Icon]) => (
              <div key={t} className="flex items-center gap-3 text-[13px]" style={{ color: "var(--text-muted)" }}>
                <div className="grid place-items-center rounded-lg" style={{ width: 32, height: 32, background: "var(--panel-2)", border: "1px solid var(--border)" }}>
                  <Icon size={16} style={{ color: "var(--accent-soft)" }} />
                </div>
                {t}
              </div>
            ))}
          </div>
        </div>
        <div className="text-[11px]" style={{ color: "var(--text-dim)" }}>© YUMAERA TRADEPLAN — Not financial advice.</div>
        <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle,rgba(139,92,246,.22),transparent 70%)" }} />
      </div>

      {/* Right auth panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="grid place-items-center rounded-xl" style={{ width: 38, height: 38, background: "linear-gradient(135deg,#8b5cf6,#6d28d9)" }}>
              <CandlestickChart size={20} color="#fff" strokeWidth={2.4} />
            </div>
            <div className="text-base font-extrabold tracking-wide">YUMAERA <span style={{ color: "var(--accent-soft)" }}>TRADEPLAN</span></div>
          </div>

          <h2 className="text-2xl font-bold">Welcome back</h2>
          <p className="mt-1.5 text-sm" style={{ color: "var(--text-muted)" }}>Sign in to access your private trading workspace.</p>

          <div className="mt-8" data-testid="google-login-container">
            {GOOGLE_CLIENT_ID ? (
              <div className="flex justify-center [color-scheme:light]">
                <GoogleLogin
                  onSuccess={onGoogleSuccess}
                  onError={() => toast.error("Google sign-in was cancelled or failed")}
                  theme="filled_black"
                  size="large"
                  shape="pill"
                  width="320"
                  text="continue_with"
                />
              </div>
            ) : (
              <button
                data-testid="google-login-button"
                onClick={legacySignIn}
                className="w-full flex items-center justify-center gap-3 rounded-xl h-12 font-semibold text-[14px] transition-transform active:scale-[.98]"
                style={{ background: "#fff", color: "#141414" }}
              >
                <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.9 2.4 30.4 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.5 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.6 7l7.1 5.5c4.2-3.9 6.9-9.6 6.9-16.5z"/><path fill="#FBBC05" d="M10.5 28.3c-.5-1.4-.8-2.9-.8-4.3s.3-3 .8-4.3l-7.9-6.1C1 16.6 0 20.2 0 24s1 7.4 2.6 10.4l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.1-5.5c-2 1.3-4.6 2.1-8.8 2.1-6.3 0-11.6-4-13.5-9.6l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
                Continue with Google
              </button>
            )}
          </div>

          <div className="mt-6 rounded-xl p-3.5 text-[12px] leading-relaxed"
            style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text-dim)" }}>
            By continuing you agree to accept our Terms of Use and Trading Risk Disclaimer on first sign-in. Trading crypto involves substantial risk of loss.
          </div>
        </div>
      </div>
    </div>
  );
}
